import React, { memo } from 'react';
import { styled } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Inline from '@atlaskit/primitives/inline';
import { xcss } from '@atlaskit/primitives/xcss';
import Box from '@atlaskit/primitives/box';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';

import { Breadcrumbs } from '@confluence/breadcrumbs/entry-points/Breadcrumbs.tsx';
import { SaveIndicator } from '@confluence/save-indicator/entry-points/SaveIndicator.ts';
import { ClassificationModalContextProvider } from '@confluence/data-classification/entry-points/ClassificationModalContext.ts';
import { ClassificationEditModal } from '@confluence/data-classification/entry-points/ClassificationEditModal.ts';
import { useShouldRenderEditorActionBarClassificationPlaceholder } from '@confluence/data-classification/entry-points/useShouldRenderEditorActionBarClassificationPlaceholder.ts';
import { EditorClassificationTagWrapper } from '@confluence/data-classification/entry-points/EditorClassificationTagWrapper.tsx';
import { EditorPrimaryToolbarComponents } from '@confluence/editor-primary-toolbar-components/entry-points/EditorPrimaryToolbarComponents.ts';
import { RequestToEditLoaderAfterPaint } from '@confluence/no-permission/entry-points/RequestToEditLoaderAfterPaint.ts';
import { ContentHeaderPresence } from '@confluence/team-presence/entry-points/ContentHeaderPresence.tsx';
import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback.ts';

import type { EditorActionBarProps } from './EditorActionBarProps.ts';

// If any change of css happens that caused EditorActionBar to have a different height,
// Update EDITOR_ACTION_BAR_HEIGHT in EditorDisplayWrapper and EditorHelpCenterComponent and EditorDraftBannerStyles
const editorActionBarWrapperStyles = xcss({
	display: 'flex',
	position: 'relative',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
	justifyContent: 'space-between',
	borderBottomColor: 'color.border',
	borderBottomStyle: 'solid',
	borderBottomWidth: 'border.width',
	minHeight: '36px',
	// editor inline coment sidebar has a zIndex of 501, and editor toolbar has a zIndex of 510, set a slighly higher zIndex would fix overlap issue with popups from inline comments and invite to share popup.
	zIndex: 'flag',
	backgroundColor: 'elevation.surface',
	paddingLeft: 'space.150',
	maxHeight: 'unset',
	alignItems: 'center',
	gap: 'space.025',
});

const hiddenStyles = xcss({
	display: 'none',
});

const overflowStyles = xcss({
	overflow: 'hidden',
});

const breadcrumbsAndClassificationTagWrapperStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	flexDirection: 'row',
	flexWrap: 'nowrap',
	flex: '0 1 auto',
	overflow: 'hidden',
	justifyContent: 'start',
	minWidth: 'fit-content',
	gap: 'space.050',

	[media.above.xs]: {
		minWidth: '96px',
	},
});

const componentsWrapperStyles = xcss({
	display: 'flex',
	minWidth: 'fit-content',
	justifyContent: 'flex-end',
	alignItems: 'center',
	flexBasis: 'unset',
	marginLeft: 'auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const VerticalSeparator = styled.div({
	borderLeft: `${token('border.width.selected')} solid ${token('color.border')}`,
	display: 'inline-block',
	width: '1px',
	height: '16px',
	margin: `0 ${token('space.075')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.span({
	marginRight: `${token('space.075')}`,
});

const EditorActionBar = ({
	spaceKey,
	editorActions,
	collabEditState,
	title,
	forceDisabled,
	userOffline,
	contentId,
	contentType,
	isContentCreationPanelOpen,
	isUnpublishedDraft,
	onClickPublish,
	onClickOpenContentCreationPanel,
	onPreviewClicked,
	saveDraft,
	collabLimitReached,
	showRevertDialog,
	retrieveEditorTitleAndContent,
	isPublishingContent,
	isSingleUserSession,
	navigateOnClose,
	triggerAnalyticsEvent,
	shouldRenderPreview,
	shouldShowRecentSpotlight,
	checkIfConditionPreventingPublish,
	flags,
	hasEditPermission,
	editorDisabled,
	isTitleFocused,
	viewMode,
	contentClassificationMetadata,
	isLivePage,
	requestToEditContent,
	hasRequestedEditPermission,
	ellipsisEditorActions,
}: EditorActionBarProps) => {
	const isDataClassificationEnabledForOrg = contentClassificationMetadata?.featureEnabled ?? false;

	const classificationProps = {
		isDataClassificationEnabledForOrg,
		spaceKey,
		contentId,
		contentType,
		contentClassificationMetadata,
		hasEditPermission,
		isLivePage,
		userOffline,
	};

	const shouldRenderClassificationPlaceholder =
		useShouldRenderEditorActionBarClassificationPlaceholder(classificationProps);

	const isCollabNetworkUnreachable =
		(editorDisabled || collabLimitReached) && !isTitleFocused && !userOffline;

	const hideSaveIndicator = viewMode || isCollabNetworkUnreachable;

	return (
		<Box
			xcss={[editorActionBarWrapperStyles, shouldRenderPreview && hiddenStyles]}
			testId="editor-action-bar"
		>
			<ClassificationModalContextProvider>
				<ClassificationEditModal
					contentId={contentId}
					spaceKey={spaceKey}
					contentClassificationMetadata={contentClassificationMetadata}
				/>
				<Box xcss={breadcrumbsAndClassificationTagWrapperStyles}>
					<Inline xcss={overflowStyles} alignBlock="center">
						<Breadcrumbs
							contentId={contentId}
							spaceKey={spaceKey}
							contentTitle={title ?? undefined}
							testId="editor-breadcrumbs-container"
						/>
					</Inline>
				</Box>
				<EditorClassificationTagWrapper
					{...classificationProps}
					shouldRenderPlaceholder={shouldRenderClassificationPlaceholder}
				/>
				<Box xcss={componentsWrapperStyles}>
					{!hideSaveIndicator && (
						<SaveIndicator
							editorActions={editorActions}
							saveError={!!collabEditState.collabEditError || userOffline}
							title={title}
						/>
					)}
					<ContentHeaderPresence
						contentId={contentId}
						contentType={contentType}
						isViewMode={viewMode}
						analyticSource="editPageScreen"
					/>
					<VerticalSeparator />
					{!hasEditPermission && (
						<ButtonWrapper>
							<RequestToEditLoaderAfterPaint
								contentId={contentId}
								requestToEditContent={requestToEditContent}
								hasRequestedEditPermission={hasRequestedEditPermission}
							/>
						</ButtonWrapper>
					)}
					<EditorPrimaryToolbarComponents
						forceDisabled={forceDisabled}
						userOffline={userOffline}
						contentId={contentId}
						contentType={contentType}
						isContentCreationPanelOpen={isContentCreationPanelOpen}
						isUnpublishedDraft={isUnpublishedDraft}
						onClickPublish={onClickPublish}
						onClickOpenContentCreationPanel={onClickOpenContentCreationPanel}
						onPreviewClicked={onPreviewClicked}
						saveDraft={saveDraft}
						spaceKey={spaceKey}
						title={title}
						collabLimitReached={collabLimitReached}
						showRevertDialog={showRevertDialog}
						retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
						isPublishingContent={isPublishingContent}
						editorActions={editorActions}
						isSingleUserSession={isSingleUserSession}
						navigateOnClose={navigateOnClose}
						triggerAnalyticsEvent={triggerAnalyticsEvent}
						shouldRenderPreview={shouldRenderPreview}
						shouldShowRecentSpotlight={shouldShowRecentSpotlight}
						checkIfConditionPreventingPublish={checkIfConditionPreventingPublish}
						flags={flags}
						viewMode={viewMode}
						hasEditPermission={hasEditPermission}
						ellipsisEditorActions={ellipsisEditorActions}
					/>
				</Box>
			</ClassificationModalContextProvider>
		</Box>
	);
};

const MemoEditorActionBar = memo(EditorActionBar);

const MemoizedEditorActionBar = (props: EditorActionBarProps): React.JSX.Element => {
	const showRevertDialog = useStableCallback(props.showRevertDialog);
	const onClickPublish = useStableCallback(props.onClickPublish);
	return (
		<MemoEditorActionBar
			{...props}
			showRevertDialog={showRevertDialog}
			onClickPublish={onClickPublish}
		/>
	);
};

export { MemoizedEditorActionBar as EditorActionBar };
